import { useMemo, useRef, useState } from 'react';

import { RestrictToElement } from '@dnd-kit/dom/modifiers';
import { useSortable } from '@dnd-kit/react/sortable';
import ClearOutlined from '@mui/icons-material/ClearOutlined';
import DragIndicator from '@mui/icons-material/DragIndicator';
import HighlightAltOutlined from '@mui/icons-material/HighlightAltOutlined';
import SettingsOutlined from '@mui/icons-material/SettingsOutlined';
import { Box, Tab, Tooltip } from '@mui/material';

import { Z_INDEX } from '@/constants/z-index';
import { useRemoveWidgetContext } from '@/context/RemoveWidgetContext';
import { canEditWidgetInDashboard } from '@/features/Dashboard/apps';
import { TABNAME_MAX_LENGTH } from '@/features/Dashboard/constants/const';
import { createNameSchema } from '@/features/Dashboard/utils/validateName';
import TooltipIconButton from '@/shared/ui/primitives/IconButton';
import { NameInput } from '@/shared/ui/primitives/NameInput';

import { MoveMenu } from './MoveMenu';

import type { Sx } from '@/types/helpers';
import type { ItemProps } from '../types';

const labelFlexSx: Sx = { display: 'flex', alignItems: 'center' };

const itemDragHandleSx: Sx = {
  width: 16,
  height: 46,
  p: 0,
  borderRadius: 0,
  backgroundColor: 'gx.response',
  zIndex: Z_INDEX.dragging,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'grab'
};

const nameInputWrapperSx: Sx = {
  maxWidth: '300px',
  '.MuiInputBase-root': { height: '46px' },
  '.MuiTextField-root': {
    minWidth: 'auto',
    display: 'flex',
    input: {
      color: 'gx.textDisabled',
      '&:focus': { color: 'gx.text' },
      '&:hover': { color: 'gx.text' }
    }
  },
  '.MuiButtonBase-root': { display: 'none' },
  '.error-message': { backgroundColor: 'gx.tileDark', px: 0.5, mt: 0 }
};

const editButtonsContainerSx: Sx = {
  display: 'flex',
  ml: 'auto',
  gap: '2px',
  '& button': {
    color: 'gx.text',
    '& svg': { color: 'gx.text' }
  }
};

const buttonHeight46Sx: Sx = { height: 46 };

const itemTabSx: Sx = {
  '&.Mui-disabled': { pointerEvents: 'auto' },
  overflow: 'visible',
  minWidth: '180px',
  maxWidth: 'initial',
  whiteSpace: 'nowrap',
  position: 'relative',
  textTransform: 'initial',
  opacity: '1 !important',
  height: 48,
  p: 0,
  borderWidth: '1px',
  borderStyle: 'dashed !important',
  borderColor: 'gx.highlight',
  alignItems: 'center',
  justifyContent: 'flex-start',
  flexDirection: 'row',
  gap: 1,
  color: 'gx.text',
  '&:hover': { color: 'inherit' },
  outline: '0px solid transparent',
  transition: 'outline-color 120ms ease, outline-width 120ms ease',
  '&[data-tab-drop-over="true"]': {
    outlineColor: 'gx.highlight',
    outlineWidth: '2px'
  }
};

/**
 * Item component representing a single tab within an app.
 * Supports drag-and-drop reordering and inline name editing.
 */
export const Item = ({
  index,
  item,
  column,
  onRemoveItem,
  container,
  isSingleItem,
  onEdit,
  currentDashboardName,
  currentTabId,
  onTabCreated,
  onRename
}: ItemProps) => {
  const elementRef = useRef<HTMLDivElement | null>(null);
  const handleRef = useRef<HTMLButtonElement | null>(null);
  const [moveItemMenuAnchor, setMoveItemMenuAnchor] = useState<HTMLElement | null>(null);

  useSortable({
    id: item.id,
    element: elementRef,
    index,
    handle: handleRef,
    type: isSingleItem ? 'singleItem' : 'item',
    accept: ['item', 'singleItem'],
    group: column,
    modifiers: [RestrictToElement.configure({ element: container.current })]
  });

  const { openRemoveModal } = useRemoveWidgetContext();

  const nameErrors = useMemo(() => {
    const res = createNameSchema('Name', TABNAME_MAX_LENGTH).safeParse(item.name);
    if (res.success) return undefined;
    return res.error;
  }, [item.name]);

  const isEditable = canEditWidgetInDashboard(item);

  return (
    <Tab
      ref={elementRef}
      disabled
      component='div'
      label={
        <Box sx={labelFlexSx}>
          <Tooltip title='Drag to reorder tab'>
            <Box
              ref={handleRef}
              className='tab-drag-handle'
              sx={itemDragHandleSx}
            >
              <DragIndicator sx={{ fontSize: '14px' }} />
            </Box>
          </Tooltip>

          <Box sx={nameInputWrapperSx}>
            <NameInput
              error={nameErrors}
              maxLength={TABNAME_MAX_LENGTH}
              value={item.name}
              onChange={onRename}
            />
          </Box>
          <Box
            className='edit-buttons__container'
            sx={editButtonsContainerSx}
          >
            <TooltipIconButton
              sx={buttonHeight46Sx}
              title='Move to ...'
              onClick={e => {
                setMoveItemMenuAnchor(e.currentTarget);
              }}
            >
              <HighlightAltOutlined />
            </TooltipIconButton>
            {isEditable && (
              <TooltipIconButton
                sx={buttonHeight46Sx}
                title='Configuration settings'
                onClick={() => {
                  onEdit(item.id);
                }}
              >
                <SettingsOutlined />
              </TooltipIconButton>
            )}
            <TooltipIconButton
              sx={buttonHeight46Sx}
              title='Remove tab'
              onClick={() => {
                openRemoveModal(() => {
                  onRemoveItem(item.id);
                });
              }}
            >
              <ClearOutlined />
            </TooltipIconButton>
            <MoveMenu
              anchorEl={moveItemMenuAnchor}
              appId={column}
              currentDashboardName={currentDashboardName}
              currentTabId={currentTabId}
              item={item}
              open={Boolean(moveItemMenuAnchor)}
              variant='item'
              onClose={() => {
                setMoveItemMenuAnchor(null);
              }}
              onRemoveFromCurrent={() => {
                onRemoveItem(item.id);
              }}
              onTabCreated={onTabCreated}
            />
          </Box>
        </Box>
      }
      sx={itemTabSx}
    />
  );
};

export default Item;
